import React, { useState, useEffect, useRef } from "react";
import CarouselItem from "./CarouselItem";
import "./Carousel.css";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "../../helpers";
import { API } from "../../constant";
import { useAuthContext } from "../../context/AuthContext";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import useReadAloud from "../Hooks/useReadAloud";

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const SpeakerIcon = ({ active }) => active ? (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
        <path d="M14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>
) : (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
    </svg>
);

const Carousel = (props) => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const initSlide = props.initialSlide || 0;
    const [activeIndex, setActiveIndex] = useState(initSlide);
    const [isLastSlide, setIsLastSlide] = useState(false);
    const [isFirstSlide, setIsFirstSlide] = useState(initSlide === 0);
    const [showWarningPopup, setShowWarningPopup] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const swiperRef = useRef(null);
    const { isSpeaking, speak, stop, spokenText, currentCharIndex } = useReadAloud();

    const handleReadAloud = () => {
        if (isSpeaking) { stop(); return; }
        const swiper = swiperRef.current;
        if (!swiper) return;
        const slideEl = swiper.slides[swiper.activeIndex];
        const contentEl = slideEl?.querySelector('.item-content');
        const text = (contentEl || slideEl)?.innerText || '';
        speak(text);
    };

    // Split spoken text into word tokens for the highlight panel
    const wordTokens = (() => {
        if (!spokenText) return [];
        const tokens = spokenText.split(/(\s+)/);
        let pos = 0;
        return tokens.map((token, i) => {
            const start = pos;
            pos += token.length;
            const isWord = !/^\s+$/.test(token);
            const isActive = isWord && currentCharIndex >= start && currentCharIndex < pos;
            return { token, isWord, isActive, key: i };
        });
    })();

    const updateIndex = (newIndex) => {
        if (newIndex < 0) {
            newIndex = 0;
        } else if (newIndex >= props.Items.length) {
            newIndex = props.Items.length - 1;
        }
        setActiveIndex(newIndex);
    };

    const sendData = () => {
        props.onDataSend(true);
    };

    useEffect(() => {
        const textareas = document.querySelectorAll('.carousel-item textarea');
        textareas.forEach(textarea => {
            textarea.addEventListener('input', (event) => {
                handleTextareaChange(event);
            });
        });
    }, []);

    // Check first slide warning on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            checkCurrentSlideWarning();
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    function handleTextareaChange(event) {
        const changedTextarea = event.target;
        const el = document.querySelector(`#${changedTextarea.name}`);
        if (el) {
            el.innerHTML = changedTextarea.value;
        }
    }

    const finishButton = () => {
        sendData();
    };

    // Check CURRENT slide for warning
    // This is called every time a slide changes, so warnings will reappear if you navigate back
    const checkCurrentSlideWarning = () => {
        const swiper = swiperRef.current;

        if (!swiper) {
            console.log("Carousel: No swiper instance available");
            return null;
        }

        const currentIndex = swiper.activeIndex;
        const currentSlide = swiper.slides[currentIndex];

        if (!currentSlide) {
            console.log("Carousel: No current slide found");
            return null;
        }

        const carouselItem = currentSlide.querySelector('.carousel-item');

        if (carouselItem && carouselItem.dataset.warning) {
            console.log(`Carousel: ⚠️ Warning found on slide ${currentIndex}:`, carouselItem.dataset.warning);
            setWarningMessage(carouselItem.dataset.warning);
            setShowWarningPopup(true);
            return carouselItem.dataset.warning;
        } else {
            console.log(`Carousel: ✓ No warning on slide ${currentIndex}`);
        }

        return null;
    };

    // Check NEXT slide for warning
    const checkNextSlideWarning = () => {
        const swiper = swiperRef.current;

        if (!swiper) {
            console.log("No swiper instance");
            return null;
        }

        const nextIndex = swiper.activeIndex + 1;

        if (nextIndex >= swiper.slides.length) {
            console.log("No more slides");
            return null;
        }

        const nextSlide = swiper.slides[nextIndex];
        const carouselItem = nextSlide.querySelector('.carousel-item');

        if (carouselItem && carouselItem.dataset.warning) {
            console.log("Warning found:", carouselItem.dataset.warning);
            return carouselItem.dataset.warning;
        }

        return null;
    };

    // Custom next button handler
    const handleNextClick = () => {
        console.log("Next button clicked");
        // Just go to next slide - warning will be checked after landing
        swiperRef.current?.slideNext();
    };

    // Custom prev button handler
    const handlePrevClick = () => {
        swiperRef.current?.slidePrev();
    };

    // Accept warning and stay on current slide
    // The warning is only hidden temporarily - it will reappear if user navigates away and comes back
    const handleAcceptWarning = () => {
        console.log("Carousel: User clicked 'Proceed' - hiding warning and staying on current slide");
        setShowWarningPopup(false);
        // Just hide the warning popup - user wants to proceed with current question
        // Warning will show again if they navigate away and come back to this slide
    };

    // Skip this section - go to next slide (warning will be checked automatically)
    const handleSkipSection = () => {
        console.log("Carousel: User clicked 'Skip' - moving to next slide");
        setShowWarningPopup(false);
        const swiper = swiperRef.current;

        if (!swiper) return;

        const nextIndex = swiper.activeIndex + 1;

        // Check if there are more slides
        if (nextIndex >= swiper.slides.length) {
            // No more slides - trigger finish if available, otherwise navigate
            console.log("Carousel: No more slides, triggering finish");
            if (props.onFinish) {
                props.onFinish();
            } else {
                navigate("/ChooseOption");
            }
            return;
        }

        // Go to next slide - onSlideChange will check for warnings
        swiper.slideNext();
    };

    const navigation = props.Settings !== undefined ? props.Settings.navigation : true;
    const pagination = props.Settings !== undefined ? props.Settings.pagination : false;

    return (
        <div className="carousel-box bg-blue">
            {props.readAloud && (
                <button
                    className={`read-aloud-btn${isSpeaking ? ' speaking' : ''}`}
                    onClick={handleReadAloud}
                    title={isSpeaking ? 'Stop reading' : 'Read aloud'}
                    aria-label={isSpeaking ? 'Stop reading' : 'Read aloud'}
                >
                    <SpeakerIcon active={isSpeaking} />
                </button>
            )}
            <div className="indicators">
                {props.Items.map((item, index) => {
                    return (
                        <button
                            key={index}
                            onClick={() => {
                                updateIndex(index);
                            }}
                            className="indicator-buttons"
                        >
                            <span
                                className={`material-symbols-outlined ${index === activeIndex ? "indicator-symbol-active" : "indicator-symbol"}`}
                            ></span>
                        </button>
                    );
                })}
            </div>
            <div className="carousel-wrap">
                <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    autoHeight={false}
                    spaceBetween={30}
                    slidesPerView={1}
                    initialSlide={initSlide}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        // Check initial state - important for single slide carousels
                        setIsLastSlide(swiper.activeIndex === swiper.slides.length - 1);
                        setIsFirstSlide(swiper.activeIndex === 0);
                    }}
                    pagination={pagination ? { clickable: true, type: 'bullets' } : false}
                    allowTouchMove={true}
                    onSlideChange={(swiper) => {
                        console.log(`Carousel: Slide changed to index ${swiper.activeIndex}`);
                        setActiveIndex(swiper.activeIndex);
                        setIsFirstSlide(swiper.activeIndex === 0);
                        setIsLastSlide(swiper.activeIndex === swiper.slides.length - 1);
                        stop();

                        // Check if the new slide has a warning
                        // This ensures warnings reappear even if previously dismissed
                        setTimeout(() => {
                            checkCurrentSlideWarning();
                        }, 100);
                    }}
                >
                    {props.Items.map((item, index) => {
                        return <SwiperSlide key={index}>{item}</SwiperSlide>;
                    })}
                </Swiper>
                <div className="carousel-controls">   
                {navigation && (
                    <>
                        {!isFirstSlide && (
                            <button className="btn-prev hover-bounce" onClick={handlePrevClick}></button>
                        )}
                        {!isLastSlide && (
                            <button className="btn-next hover-bounce" onClick={handleNextClick}></button>
                        )}
                    </>
                )}

                {isLastSlide && (
                    <button
                        className="finish-btn button-right-arrow hover-bounce"
                        onClick={() => props.onFinish ? props.onFinish() : navigate("/ChooseOption")}
                    >
                        {props.finishButtonText || "Finish"} <span className="arrow-right"></span>
                    </button>
                )}
                </div>
            </div>

            {/* Word-highlight reading panel */}
            {props.readAloud && spokenText && (
                <div className="reading-panel">
                    {wordTokens.map(({ token, isActive, key }) => (
                        <span key={key} className={isActive ? 'reading-word-active' : ''}>
                            {token}
                        </span>
                    ))}
                </div>
            )}

            {/* Warning Popup */}
            {showWarningPopup && (
                <div className="warning-popup-overlay fade-in-down">
                    <div className="warning-popup">
                        <div className="warning-icon">⚠️</div>
                        <h3>Warning</h3>
                        <p>{warningMessage}</p>
                        <div className="warning-buttons">
                            <button className="btn-accept hover-bounce" onClick={handleAcceptWarning}>
                                I want to proceed
                            </button>
                            <button className="btn-decline hover-bounce" onClick={handleSkipSection}>
                                Skip This Section
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Carousel;